import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import { auth, hasCreatorAccess, refreshAuth } from './state'
import { standaloneMode } from './api'
import HomeView from './views/HomeView.vue'
import ExploreView from './views/ExploreView.vue'
import SurveyDetailView from './views/SurveyDetailView.vue'
import TakeSurveyView from './views/TakeSurveyView.vue'
import ResultView from './views/ResultView.vue'
import AuthView from './views/AuthView.vue'
import CreatorView from './views/CreatorView.vue'
import BuilderView from './views/BuilderView.vue'
import AnalyticsView from './views/AnalyticsView.vue'
import AdminView from './views/AdminView.vue'
import AccountView from './views/AccountView.vue'
import NotFoundView from './views/NotFoundView.vue'

const router = createRouter({
  history: import.meta.env.VITE_STANDALONE === 'true' ? createWebHashHistory() : createWebHistory(),
  scrollBehavior: () => ({ top: 0, behavior: 'smooth' }),
  routes: [
    { path: '/', name: 'home', component: HomeView },
    { path: '/explore', name: 'explore', component: ExploreView },
    { path: '/surveys/:slug', name: 'survey-detail', component: SurveyDetailView },
    { path: '/surveys/:slug/take', name: 'survey-take', component: TakeSurveyView, meta: { immersive: true } },
    { path: '/results/:id', name: 'result', component: ResultView },
    { path: '/login', name: 'login', component: AuthView },
    { path: '/local-create', name: 'local-create', component: CreatorView, meta: { localCreator: true } },
    { path: '/local-create/new', name: 'local-creator-new', component: BuilderView, meta: { localCreator: true } },
    { path: '/local-create/:id/edit', name: 'local-creator-edit', component: BuilderView, meta: { localCreator: true } },
    { path: '/local-create/:id/analytics', name: 'local-creator-analytics', component: AnalyticsView, meta: { localCreator: true } },
    { path: '/creator', name: 'creator', component: CreatorView, meta: { auth: true, creator: true } },
    { path: '/creator/new', name: 'creator-new', component: BuilderView, meta: { auth: true, creator: true } },
    { path: '/creator/:id/edit', name: 'creator-edit', component: BuilderView, meta: { auth: true, creator: true } },
    { path: '/creator/:id/analytics', name: 'creator-analytics', component: AnalyticsView, meta: { auth: true, creator: true } },
    { path: '/account', name: 'account', component: AccountView, meta: { auth: true } },
    { path: '/account/:action', name: 'account-action', component: AccountView },
    { path: '/admin', name: 'admin', component: AdminView, meta: { auth: true, admin: true } },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView },
  ],
})

router.beforeEach(async (to) => {
  if (!auth.ready) await refreshAuth()
  if (standaloneMode && to.path === '/') return '/creator'
  if (to.meta.auth && !auth.user) return { path: '/login', query: { redirect: to.fullPath } }
  if (auth.user?.mustChangePassword && to.name !== 'account' && to.name !== 'account-action') return { path: '/account', query: { 'force-password-change': '1' } }
  if (auth.user && ['admin', 'safety_admin', 'super_admin'].includes(auth.user.role) && !auth.user.totpEnabled && to.name !== 'account' && to.name !== 'account-action') return { path: '/account', query: { 'setup-2fa': '1' } }
  if (to.meta.creator && !hasCreatorAccess()) return { path: '/explore', query: { notice: 'creator-invite-required' } }
  if (to.meta.admin && !['admin', 'safety_admin', 'super_admin'].includes(auth.user?.role || '')) return '/'
  if (to.path === '/login' && auth.user) return auth.user.mustChangePassword ? '/account?force-password-change=1' : !auth.user.totpEnabled ? '/account?setup-2fa=1' : '/admin'
})

const pageTitles: Record<string, string> = {
  home: '首页',
  explore: '心光馆与量域馆',
  'survey-detail': '问卷详情',
  'survey-take': '填写问卷',
  result: '结果报告',
  login: '站长与管理员入口',
  'local-create': '在线本地创作台',
  'local-creator-new': '新建本地问卷',
  'local-creator-edit': '编辑本地问卷',
  'local-creator-analytics': '本地问卷数据',
  creator: '管理员内容工作台',
  'creator-new': '新建问卷',
  'creator-edit': '编辑问卷',
  'creator-analytics': '问卷数据中心',
  account: '账号中心',
  'account-action': '账号操作',
  admin: '管理与审核',
  'not-found': '页面未找到',
}

router.afterEach((to) => {
  const title = (standaloneMode && to.name === 'creator') || to.meta.localCreator
    ? (to.name === 'local-creator-new' ? '新建本地问卷' : to.name === 'local-creator-edit' ? '编辑本地问卷' : to.name === 'local-creator-analytics' ? '本地问卷数据' : '本地创作台')
    : pageTitles[String(to.name)] || '心光量域'
  document.title = `${title} · 心光量域 LUMISCALE`
})

export default router
